import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { X, Upload, Image as ImageIcon, CheckCircle, CircleAlert, Loader2 } from 'lucide-react';
import { useRepositories } from '../hooks/useRepositories';
import { getApiErrorMessage } from '../utils/errors';
import type { PendingPhoto } from '../domain/listings';

interface ImageFile {
  id?: string;
  file: File;
  preview: string;
  uploading: boolean;
  uploaded: boolean;
  error?: string;
  pendingImage?: PendingPhoto;
  progress?: number;
}

interface ImageUploadProps {
  /** How many more may be added here. */
  maxImages?: number;
  /** Photos the listing already has, counted toward its limit in what's shown. */
  attachedCount?: number;
  maxFileSize?: number;
  onImagesChange?: (pendingImages: PendingPhoto[]) => void;
  autoUpload?: boolean; // Start uploading immediately when images are selected
}

export default function ImageUpload({
  maxImages = 20,
  attachedCount = 0,
  maxFileSize = 20 * 1024 * 1024,
  onImagesChange,
  autoUpload = true,
}: ImageUploadProps) {
  const { photoUpload } = useRepositories();
  const [images, setImages] = useState<ImageFile[]>([]);
  const [error, setError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Latest images, read by the unmount cleanup below without making it a dep
  // (so the cleanup runs once, on unmount, against the real current list).
  const imagesRef = useRef<ImageFile[]>([]);
  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  // Revoke object URLs on unmount to free memory.
  useEffect(() => {
    return () => {
      imagesRef.current.forEach((image) => {
        URL.revokeObjectURL(image.preview);
      });
    };
  }, []);

  // Notify parent when pending images change
  useEffect(() => {
    if (onImagesChange) {
      const pendingImages = images
        .filter((img) => img.uploaded && img.pendingImage && img.pendingImage.id)
        .map((img) => img.pendingImage!)
        .filter((pendingImg) => pendingImg.id != null && pendingImg.id !== '');
      onImagesChange(pendingImages);
    }
  }, [images, onImagesChange]);

  // Defined before handleFileSelect so it can sit in that callback's deps
  // without a TDZ. Stable (no external deps) — it works off the index and the
  // functional setImages updater, not the images array.
  const uploadImage = useCallback(
    async (imageFile: ImageFile, index: number) => {
      // Mark as uploading
      setImages((prev) => {
        const updated = [...prev];
        if (updated[index]) {
          updated[index] = { ...updated[index], uploading: true, error: undefined, progress: 0 };
        }
        return updated;
      });

      try {
        // Simulate progress for better UX
        const progressInterval = setInterval(() => {
          setImages((prev) => {
            const updated = [...prev];
            if (updated[index] && updated[index].uploading) {
              updated[index] = {
                ...updated[index],
                progress: Math.min((updated[index].progress || 0) + 10, 90),
              };
            }
            return updated;
          });
        }, 100);

        // Upload to backend
        const pendingImage = await photoUpload.upload(imageFile.file);

        clearInterval(progressInterval);

        // Mark as uploaded
        setImages((prev) => {
          const updated = [...prev];
          if (updated[index]) {
            updated[index] = {
              ...updated[index],
              uploading: false,
              uploaded: true,
              progress: 100,
              pendingImage,
            };
          }
          return updated;
        });
      } catch (err) {
        console.error('Upload error:', err);
        setImages((prev) => {
          const updated = [...prev];
          if (updated[index]) {
            updated[index] = {
              ...updated[index],
              uploading: false,
              error: getApiErrorMessage(err, 'Upload failed'),
              progress: 0,
            };
          }
          return updated;
        });
      }
    },
    [photoUpload],
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      setError('');

      // Check total image count
      if (images.length + files.length > maxImages) {
        setError(`A listing can have up to ${attachedCount + maxImages} photos`);
        return;
      }

      // Validate and create preview for each file
      const newImages: ImageFile[] = [];
      for (const file of files) {
        // Check file type
        if (!file.type.startsWith('image/')) {
          setError(`${file.name} is not an image file`);
          continue;
        }

        // Check file size
        if (file.size > maxFileSize) {
          setError(`${file.name} exceeds ${maxFileSize / (1024 * 1024)}MB limit`);
          continue;
        }

        // Create preview URL
        const preview = URL.createObjectURL(file);
        newImages.push({
          file,
          preview,
          uploading: false,
          uploaded: false,
          progress: 0,
        });
      }

      setImages((prev) => {
        const updated = [...prev, ...newImages];

        // Auto-upload if enabled
        if (autoUpload) {
          // Upload each new image
          newImages.forEach((newImage, index) => {
            const imageIndex = prev.length + index;
            uploadImage(newImage, imageIndex);
          });
        }

        return updated;
      });

      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [images.length, maxImages, attachedCount, maxFileSize, autoUpload, uploadImage],
  );

  const removeImage = useCallback(
    (index: number) => {
      setImages((prev) => {
        const updated = [...prev];
        const imageToRemove = updated[index];

        // Delete from backend if it was uploaded
        if (imageToRemove.uploaded && imageToRemove.pendingImage) {
          photoUpload.delete(imageToRemove.pendingImage.id).catch(console.error);
        }

        // Revoke preview URL to free memory
        URL.revokeObjectURL(imageToRemove.preview);
        updated.splice(index, 1);
        return updated;
      });
    },
    [photoUpload],
  );

  const uploadAll = useCallback(async () => {
    const pendingUploads = images
      .map((img, index) => ({ img, index }))
      .filter(({ img }) => !img.uploaded && !img.uploading);

    for (const { img, index } of pendingUploads) {
      await uploadImage(img, index);
    }
  }, [images, uploadImage]);

  const retryUpload = useCallback(
    (index: number) => {
      const imageFile = images[index];
      if (imageFile && imageFile.error) {
        uploadImage(imageFile, index);
      }
    },
    [images, uploadImage],
  );

  return (
    <div className="space-y-4">
      {/* Upload button */}
      <div className="flex items-center gap-4">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={images.length >= maxImages}
        >
          <Upload className="mr-2 size-4" />
          Select Images
        </Button>
        <span className="text-sm text-muted-foreground">
          {attachedCount + images.length} / {attachedCount + maxImages} photos
        </span>
        {!autoUpload && images.some((img) => !img.uploaded && !img.uploading) && (
          <Button
            type="button"
            onClick={uploadAll}
            disabled={images.every((img) => img.uploaded || img.uploading)}
          >
            Upload All
          </Button>
        )}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Image previews */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {images.map((image, index) => (
            <div key={index} className="group relative">
              <div className="aspect-square overflow-hidden rounded-2xl bg-muted">
                <img
                  src={image.preview}
                  alt={`Preview ${index + 1}`}
                  className="size-full object-cover"
                />

                {/* Upload progress overlay */}
                {image.uploading && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-foreground/50">
                    <Loader2 className="mb-2 size-6 animate-spin text-background" />
                    <div className="text-sm text-background">{image.progress || 0}%</div>
                    <div className="mt-1 h-1 w-16 rounded-full bg-background/30">
                      <div
                        className="h-1 rounded-full bg-primary transition-all duration-300"
                        style={{ width: `${image.progress || 0}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Success indicator */}
                {image.uploaded && (
                  <div className="absolute right-2 top-2 rounded-full bg-secondary p-1 text-secondary-foreground">
                    <CheckCircle className="size-4" />
                  </div>
                )}

                {/* Error indicator */}
                {image.error && (
                  <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1 bg-destructive p-1 text-xs text-destructive-foreground">
                    <CircleAlert className="size-3 shrink-0" aria-hidden="true" />
                    {image.error}
                  </div>
                )}
              </div>

              {/* Remove button */}
              <button
                type="button"
                onClick={() => removeImage(index)}
                className="absolute left-2 top-2 rounded-full bg-destructive p-1 text-destructive-foreground opacity-0 transition-opacity group-hover:opacity-100"
              >
                <X className="size-4" />
              </button>

              {/* Retry button for failed uploads */}
              {image.error && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="absolute inset-x-2 bottom-2 text-xs"
                  onClick={() => retryUpload(index)}
                >
                  Retry
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {images.length === 0 && (
        <div className="rounded-3xl border-2 border-dashed border-primary/30 bg-muted p-8 text-center">
          <ImageIcon className="mx-auto size-12 text-primary/60" />
          <p className="mt-2 text-sm text-muted-foreground">
            Drop images here or click "Select Images" to start uploading
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Images will upload immediately • Maximum {attachedCount + maxImages} photos, up to{' '}
            {maxFileSize / (1024 * 1024)}MB each
          </p>
        </div>
      )}
    </div>
  );
}
